export function parseCoordinate(value) {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function buildLocationKey(lat, lng, precision = 6) {
  const parsedLat = parseCoordinate(lat);
  const parsedLng = parseCoordinate(lng);
  if (parsedLat === null || parsedLng === null) return null;
  return `${parsedLat.toFixed(precision)},${parsedLng.toFixed(precision)}`;
}

export function groupDistributionPointsByLocation(points = []) {
  const groups = [];
  const map = new Map();

  points.forEach((point) => {
    const key = buildLocationKey(point.lat, point.lng);
    if (!key) return;

    if (!map.has(key)) {
      const group = {
        key,
        position: {
          lat: parseCoordinate(point.lat),
          lng: parseCoordinate(point.lng),
        },
        points: [],
      };
      map.set(key, group);
      groups.push(group);
    }

    map.get(key).points.push(point);
  });

  return groups.map((group) => ({
    ...group,
    count: group.points.length,
    isCluster: group.points.length > 1,
  }));
}

export function getDistributionPointLabel(point) {
  if (!point) return "-";
  return (
    point.distrbution_point_name ||
    point.name ||
    `#${point.id || "-"}`
  );
}

export function createDistributionPointMarkerIcon(count = 1) {
  const showCount = count > 1;
  const countBadge = showCount
    ? `<circle cx="32" cy="10" r="9" fill="#fff" stroke="#E53935" stroke-width="2"/>
       <text x="32" y="14" text-anchor="middle" font-size="10" font-weight="700" fill="#E53935">${count}</text>`
    : "";

  return {
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
          <circle cx="20" cy="20" r="18" fill="#E53935"/>
          <circle cx="20" cy="20" r="14" fill="white"/>
          <circle cx="20" cy="20" r="6" fill="#E53935"/>
          <path d="M20 38 L15 46 L25 46 Z" fill="#E53935"/>
          ${countBadge}
        </svg>
      `),
    scaledSize: { width: 40, height: 50 },
    anchor: { x: 20, y: 50 },
  };
}

export function createDelegateMarkerIcon(delegate, isTracking = false) {
  const size = isTracking ? 60 : 50;
  const heightSize = isTracking ? 70 : 60;
  const color = isTracking ? "#00C853" : "#4285F4";

  return {
    url:
      "data:image/svg+xml;charset=UTF-8," +
      encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${heightSize}" viewBox="0 0 ${size} ${heightSize}">
          <defs>
            <filter id="shadow-${delegate.id}" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="0" dy="2" result="offsetblur"/>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}" fill="${color}" filter="url(#shadow-${delegate.id})"/>
          <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 7}" fill="white"/>
          <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 17}" fill="${color}"/>
          <path d="M${size / 2} ${size - 5} L${size / 2 - 5} ${heightSize - 5} L${size / 2 + 5} ${heightSize - 5} Z" fill="${color}"/>
        </svg>
      `),
    scaledSize: { width: size, height: heightSize },
    anchor: { x: size / 2, y: heightSize - 5 },
  };
}

export function fitMapToMarkers(map, delegates = [], distributionPoints = []) {
  if (!map || !window.google) return;

  const bounds = new google.maps.LatLngBounds();
  let hasMarkers = false;

  delegates.forEach((delegate) => {
    if (delegate.lat == null || delegate.lng == null) return;
    bounds.extend({ lat: delegate.lat, lng: delegate.lng });
    hasMarkers = true;
  });

  distributionPoints.forEach((point) => {
    const lat = parseCoordinate(point.lat);
    const lng = parseCoordinate(point.lng);
    if (lat === null || lng === null) return;
    bounds.extend({ lat, lng });
    hasMarkers = true;
  });

  if (!hasMarkers) return;

  map.fitBounds(bounds);

  const listener = google.maps.event.addListener(map, "idle", () => {
    if (map.getZoom() > 15) {
      map.setZoom(15);
    }
    google.maps.event.removeListener(listener);
  });
}

export function filterActiveDistributionPoints(points = []) {
  return points.filter((point) => {
    const lat = parseCoordinate(point.lat);
    const lng = parseCoordinate(point.lng);
    return point.is_active && lat !== null && lng !== null;
  });
}

export function mapActiveDeliveringToDelegate(item) {
  if (!item?.id) return null;

  const lat = parseCoordinate(item.lat);
  const lng = parseCoordinate(item.lng);
  if (lat === null || lng === null) return null;

  const avatar =
    typeof item.avatar === "string" ? item.avatar : item.avatar?.url ?? null;

  return {
    id: item.id,
    name: item.name,
    avatar,
    phone: item.mobile,
    mobile: item.mobile,
    lat,
    lng,
    address: item.address,
    bundle_id: item.current_bundle?.id ?? null,
    current_bundle: item.current_bundle ?? null,
    is_active: true,
  };
}
